import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Armchair,
  ArrowRight,
  Car,
  MapPin,
  MapPinned,
  Route,
  Smartphone,
  TrendingUp,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  APP_STORE_URL,
  DURI_APP_URL,
  PLAY_STORE_URL,
  categoryTone,
  categoryToneClass,
} from "@/data/availableRidesData";
import { getBrowserLocation, getCityStateFromLatLng } from "@/utils/geoLocationUtils";
import { LOCATION_UNKNOWN_DISPLAY } from "@/utils/locationDisplay";
import { formatIsoDateToUsMdY } from "@/utils/routesAdminDateFormat";
import { ROUTE_PATHS } from "@/routes/routePaths";
import { MarketingRideSearchBar } from "@/components/common/MarketingRideSearchBar";
import {
  getHostSummary,
  getHostWaitingRoutes,
  type HostRouteCardDto,
  type HostSummaryResponse,
  type HostWaitingRoutesResponse,
} from "@/services/availableHostsService";
import { getWaitingRoutes } from "@/services/waitingGuestsService";

function HostOfferCard({ card }: { card: HostRouteCardDto }) {
  const purpose = card.purpose?.trim();
  const tone = purpose ? categoryTone(purpose) : "slate";
  const seats = card.seatsAvailable ?? 0;
  const fallbackDate =
    card.rideDateTime && !Number.isNaN(new Date(card.rideDateTime).getTime())
      ? new Date(card.rideDateTime).toLocaleDateString("en-IN", {
          year: "numeric",
          month: "short",
          day: "2-digit",
          timeZone: "Asia/Kolkata",
        })
      : "";
  const displayDate = card.date?.trim() || fallbackDate;
  const displayPrice =
    typeof card.price === "number" ? `₹${Math.round(card.price).toLocaleString("en-IN")}` : null;
  const displayVehicle = card.vehicle?.trim() || null;

  return (
    <article className="group rounded-xl border border-border bg-card p-4 transition-shadow hover:shadow-md">
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-muted-foreground">{displayDate}</span>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="rounded-md bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
          {seats} seat{seats === 1 ? "" : "s"} available
        </span>
        {purpose && (
          <span
            className={cn(
              "rounded-md px-2 py-0.5 text-[10px] font-semibold",
              categoryToneClass[tone]
            )}
          >
            {purpose}
          </span>
        )}
      </div>
      {(displayVehicle || displayPrice) && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {displayVehicle ? (
            <span className="rounded-md bg-muted px-2 py-0.5">{displayVehicle}</span>
          ) : null}
          {displayPrice ? (
            <span className="rounded-md bg-primary/10 px-2 py-0.5 font-semibold text-primary">
              {displayPrice}
            </span>
          ) : null}
        </div>
      )}
      <a
        href={DURI_APP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-2 text-sm font-semibold text-primary-foreground opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100"
      >
        Book on App <ArrowRight className="h-3.5 w-3.5" />
      </a>
    </article>
  );
}

function routeEndpoints(r: {
  from?: string;
  to?: string;
  fromCity?: string;
  toCity?: string;
}): { from: string; to: string } {
  return {
    from: r.from ?? r.fromCity ?? "",
    to: r.to ?? r.toCity ?? "",
  };
}

export function AvailableRidesPageContent() {
  const [fromCity, setFromCity] = useState("");
  const [showAllCities, setShowAllCities] = useState(false);

  const [summary, setSummary] = useState<HostSummaryResponse | null>(null);
  const [routesPayload, setRoutesPayload] = useState<HostWaitingRoutesResponse | null>(null);

  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [isRoutesLoading, setIsRoutesLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      setIsSummaryLoading(true);
      setIsRoutesLoading(true);
      setErrorMessage("");

      const params = new URLSearchParams(window.location.search);
      const urlFrom = (params.get("fromCity") ?? params.get("from") ?? "").trim();
      const urlTo = (params.get("toCity") ?? params.get("to") ?? "").trim();
      const urlDate = (params.get("date") ?? "").trim();

      let resolvedCity = urlFrom;
      if (!resolvedCity) {
        try {
          const { lat, lng } = await getBrowserLocation({ timeout: 5000 });
          const geo = await getCityStateFromLatLng(lat, lng);
          if (geo.city && geo.city !== "Not detected") {
            resolvedCity = geo.city;
          }
        } catch {
          // Leave empty; summary can load without `location`, routes use date only if needed
        }
      }

      if (cancelled) return;
      setFromCity(resolvedCity);

      try {
        const summaryData = await getHostSummary(resolvedCity || undefined);
        if (!cancelled) setSummary(summaryData);
      } catch {
        if (!cancelled) {
          setErrorMessage("Unable to load host summary.");
        }
      } finally {
        if (!cancelled) setIsSummaryLoading(false);
      }

      try {
        const routesData = await getHostWaitingRoutes({
          fromCity: resolvedCity || undefined,
          toCity: urlTo || undefined,
          date: urlDate ? formatIsoDateToUsMdY(urlDate) : undefined,
          page: 1,
        });
        if (!cancelled) setRoutesPayload(routesData);

        await getWaitingRoutes({
          fromCity: resolvedCity || undefined,
          page: 1,
        });
      } catch {
        if (!cancelled) {
          setErrorMessage((prev) => prev || "Unable to load routes.");
        }
      } finally {
        if (!cancelled) setIsRoutesLoading(false);
      }
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const routeSections = useMemo(() => {
    if (!routesPayload?.routes?.length) return [];
    return routesPayload.routes.map((route, routeIndex) => {
      const { from, to } = routeEndpoints(route);
      const cards = route.cards ?? [];
      const seatsAvailableTotal = cards.reduce(
        (sum, c) => sum + (typeof c.seatsAvailable === "number" ? c.seatsAvailable : 0),
        0
      );
      return {
        id: `${from}-${to}-${routeIndex}`,
        from,
        to,
        rideCount: route.count ?? route.routeCount ?? cards.length,
        seatsAvailableTotal,
        cards,
      };
    });
  }, [routesPayload]);

  const visibleRideCount = useMemo(() => {
    return routeSections.reduce((acc, s) => acc + s.cards.length, 0);
  }, [routeSections]);

  const otherRoutes = routesPayload?.otherRoutes ?? [];
  const popularRoutes = summary?.mostPopularRoutes ?? [];
  const rideCategories = summary?.rideCategories ?? [];

  const departureLabel = showAllCities ? "India" : fromCity.trim() || LOCATION_UNKNOWN_DISPLAY;

  const handleShowAllCitiesToggle = async () => {
    const next = !showAllCities;
    setShowAllCities(next);
    setIsSummaryLoading(true);
    setErrorMessage("");
    try {
      const data = await getHostSummary(next ? undefined : fromCity.trim() || undefined);
      setSummary(data);
    } catch {
      setErrorMessage("Unable to refresh host summary.");
    } finally {
      setIsSummaryLoading(false);
    }
  };

  return (
    <div className="available-rides-page bg-background text-foreground">
      <section className="bg-hero-overlay px-4 py-16 text-primary-foreground sm:py-24">
        <div className="container mx-auto max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/10 px-4 py-1.5 text-sm font-medium">
            <Car className="h-4 w-4" /> For Guests — Available Rides
          </div>
          <h1 className="mb-4 text-3xl font-extrabold leading-tight sm:text-5xl">
            Available Rides
          </h1>
          <p className="mb-8 text-lg text-primary-foreground/80">
            Browse {summary?.totalHostsWaiting ?? 0} hosted rides across India. Save up to 70%
            compared to solo travel.
          </p>
        </div>
      </section>

      <main className="container py-8">
        <div className="mb-6 rounded-xl border border-primary/20 bg-secondary p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <MapPinned className="mt-0.5 h-6 w-6 shrink-0 text-primary" />
              <div>
                <p className="text-base font-semibold text-foreground">
                  👋 Hey! I see you&apos;re in {fromCity.trim() || LOCATION_UNKNOWN_DISPLAY}
                </p>
                <p className="text-sm text-muted-foreground">
                  {showAllCities
                    ? "Explore rides across India. Use the search below to narrow your route."
                    : `Here are rides departing from ${
                        fromCity.trim() || LOCATION_UNKNOWN_DISPLAY
                      }. Pick cities in the search row anytime.`}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => void handleShowAllCitiesToggle()}
              className="shrink-0 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-card"
            >
              {showAllCities ? "Focus one city" : "Show all cities"}
            </button>
          </div>
        </div>

        <div className="mb-8">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="mb-2 flex items-center gap-2 text-primary">
                <Car className="h-5 w-5" />
                <span className="text-sm font-medium text-muted-foreground">Hosts / rides</span>
              </div>
              <p className="text-3xl font-extrabold text-foreground">
                {isSummaryLoading ? "..." : summary?.totalHostsWaiting ?? 0}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="mb-2 flex items-center gap-2 text-primary">
                <Armchair className="h-5 w-5" />
                <span className="text-sm font-medium text-muted-foreground">Seats available</span>
              </div>
              <p className="text-3xl font-extrabold text-foreground">
                {isSummaryLoading ? "..." : summary?.seatsAvailable ?? 0}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="mb-3 flex items-center gap-2 text-primary">
                <TrendingUp className="h-5 w-5" />
                <span className="text-sm font-medium text-muted-foreground">
                  Most popular routes
                </span>
              </div>
              <ul className="space-y-1.5">
                {popularRoutes.map((r) => (
                  <li key={`${r.from}-${r.to}`} className="flex items-center justify-between text-xs">
                    <span className="truncate text-foreground">
                      {r.from} → {r.to}
                    </span>
                    <span className="ml-2 shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-secondary-foreground">
                      {r.count}
                    </span>
                  </li>
                ))}
                {!isSummaryLoading && popularRoutes.length === 0 && (
                  <li className="text-xs text-muted-foreground">No data yet</li>
                )}
              </ul>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="mb-3 flex items-center gap-2 text-primary">
                <Route className="h-5 w-5" />
                <span className="text-sm font-medium text-muted-foreground">Ride Purpose Category</span>
              </div>
              <ul className="space-y-1.5">
                {rideCategories.map((row) => (
                  <li
                    key={row.purpose}
                    className="flex items-center justify-between gap-2 text-xs"
                  >
                    <span className="truncate text-foreground">{row.purpose?.trim() ?? ""}</span>
                    <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-secondary-foreground">
                      {row.count}
                    </span>
                  </li>
                ))}
                {!isSummaryLoading && rideCategories.length === 0 && (
                  <li className="text-xs text-muted-foreground">No categories yet</li>
                )}
              </ul>
            </div>
          </div>
        </div>

        <MarketingRideSearchBar idPrefix="available-rides" />

        {errorMessage && (
          <p className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {errorMessage}
          </p>
        )}

        <p className="mb-6 text-sm text-muted-foreground">
          Showing <strong className="text-foreground">{visibleRideCount}</strong> ride slots from{" "}
          <strong className="text-foreground">{departureLabel}</strong>
          {isRoutesLoading && " (loading…)"}
        </p>

        <div className="space-y-8">
          {routeSections.map((section) => (
            <section key={section.id}>
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                <h2 className="text-lg font-bold text-foreground">
                  {section.from} → {section.to}
                </h2>
                <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground">
                  {(() => {
                    const n =
                      section.seatsAvailableTotal > 0
                        ? section.seatsAvailableTotal
                        : section.rideCount;
                    return `${n} ${n === 1 ? "seat" : "seats"}`;
                  })()}
                </span>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {section.cards.map((card, cardIndex) => (
                  <HostOfferCard key={`${section.id}-${cardIndex}`} card={card} />
                ))}
              </div>
              {section.cards.length === 0 && (
                <p className="mt-2 text-sm text-muted-foreground">
                  No ride listings for this corridor in the selected window.
                </p>
              )}
            </section>
          ))}
          {!isRoutesLoading &&
            !isRoutesLoading &&
            routeSections.length === 0 &&
            otherRoutes.length === 0 && (
              <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                No ride slots matched this view. Try different cities or dates in search.
              </div>
            )}
        </div>

        <section className="mt-16">
          <h2 className="mb-2 text-xl font-bold text-foreground">🗺️ Explore other popular routes</h2>
          <p className="mb-6 text-sm text-muted-foreground">
            More corridors with available seats from hosts near you.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {otherRoutes.map((card) => (
              <div
                key={`${card.fromCity}-${card.toCity}`}
                className="rounded-xl border border-border bg-card p-4"
              >
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-bold text-foreground">
                    {card.fromCity} → {card.toCity}
                  </h3>
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-secondary-foreground">
                    {card.totalSeatsAvailable}{" "}
                    {card.totalSeatsAvailable === 1 ? "seat" : "seats"}
                  </span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Total seats available: {card.totalSeatsAvailable}
                </p>
              </div>
            ))}
          </div>
          {!isRoutesLoading && otherRoutes.length === 0 && routeSections.length > 0 && (
            <p className="mt-4 text-sm text-muted-foreground">No additional routes in this view.</p>
          )}
        </section>

        <section className="mt-16 rounded-2xl border border-border bg-card p-8 sm:p-12">
          <h2 className="mb-8 text-center text-2xl font-bold text-foreground">
            How to Book a Ride on Duri
          </h2>
          <div className="grid gap-6 sm:grid-cols-3">
            {[
              {
                step: "1",
                title: "Search",
                body: "Find rides between your cities on this page or in the Duri app.",
              },
              {
                step: "2",
                title: "Book or Wait",
                body: "Book instantly if seats are available, or save as 'Waiting' to notify hosts.",
              },
              {
                step: "3",
                title: "Travel & Save",
                body: "Share the ride, split costs, and enjoy a safe, verified journey.",
              },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-lg font-bold text-primary-foreground">
                  {item.step}
                </div>
                <h3 className="mb-1 text-base font-bold text-foreground">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-12">
          <section className="rounded-2xl bg-guest-gradient p-8 sm:p-12">
            <div className="mx-auto flex max-w-2xl flex-col items-center gap-6 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-foreground/20">
                <Smartphone className="h-7 w-7 text-primary-foreground" />
              </div>
              <h2 className="text-2xl font-bold text-primary-foreground sm:text-3xl">
                Looking for a Ride? Download Duri!
              </h2>
              <p className="text-primary-foreground/80">
                Find affordable rides between cities. Book instantly or save as waiting.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-lg bg-primary-foreground px-5 py-3 text-sm font-semibold text-foreground transition-opacity hover:opacity-90"
                >
                  Google Play <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-lg border-2 border-primary-foreground/30 px-5 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                >
                  App Store <ArrowRight className="h-4 w-4" />
                </a>
              </div>
              <p className="text-sm text-primary-foreground/70">
                Also see{" "}
                <Link
                  to={ROUTE_PATHS.RIDE_REQUESTS}
                  className="font-semibold underline underline-offset-2"
                >
                  Ride Requests
                </Link>{" "}
                for travelers seeking a seat.
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
